// src/components/private/HouseholdDetails.js
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, FormControl, Button, Modal } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmationModal from '../common_components/ConfirmationModal';
import { COUNTRY_CHOICES } from '../common_components/choices';

const HouseholdDetails = ({ show, handleClose, refreshHouseholds, selectedHousehold }) => {
    const token = localStorage.getItem('token');

    const [householdData, setHouseholdData] = useState({
        name: '',
        street_address: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'DE', // Defaulting to Germany, you can adjust this as needed
    });

    const [showConfirmModal, setShowConfirmModal] = useState(false); // State to manage the confirmation modal

    const isEditable = selectedHousehold ? selectedHousehold.is_primary_member : true;

    useEffect(() => {
        if (selectedHousehold) {
            setHouseholdData({
                name: selectedHousehold.name,
                street_address: selectedHousehold.street_address,
                city: selectedHousehold.city,
                state: selectedHousehold.state,
                postal_code: selectedHousehold.postal_code,
                country: selectedHousehold.country,
            });
        } else {
            resetForm();
        }
    }, [selectedHousehold]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setHouseholdData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const resetForm = () => {
        setHouseholdData({
            name: '',
            street_address: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'DE',
        });
    };

    const handleHouseholdSave = async (event) => {
        event.preventDefault();

        try {
            if (selectedHousehold) {
                // Update existing household
                await axios.patch(`${process.env.REACT_APP_BACKEND_API_URL}/users/households/${selectedHousehold.id}/`, householdData, {
                    headers: {
                        Authorization: `Token ${token}`,
                    },
                });
                toast.success('Household updated successfully!');
            } else {
                // Add new household
                await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/users/households/`, householdData, {
                    headers: {
                        Authorization: `Token ${token}`,
                    },
                });
                toast.success('Household added successfully!');
            }
            refreshHouseholds();
            handleClose();
            resetForm();
        } catch (error) {
            console.error('Error saving household:', error);
            toast.error('Error saving household.');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/users/households/${selectedHousehold.id}/`, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            toast.success('Household deleted successfully!');
            refreshHouseholds();
            handleClose();
            resetForm();
        } catch (error) {
            console.error('Error deleting household:', error);
            toast.error('Error deleting household.');
        }
    };

    const handleDeleteClick = () => {
        setShowConfirmModal(true); // Show the confirmation modal
    };

    const handleConfirmDelete = () => {
        setShowConfirmModal(false);
        handleDelete();
    };

    return (
        <>
            <Modal show={show} onHide={() => { resetForm(); handleClose(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedHousehold ? 'Edit Household' : 'Add New Household'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleHouseholdSave}>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <FormControl
                                    placeholder="Household Name"
                                    aria-label="Household Name"
                                    name="name"
                                    value={householdData.name}
                                    onChange={handleChange}
                                    required
                                    disabled={!isEditable}
                                />
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Street Address</Form.Label>
                                <FormControl
                                    placeholder="Street Address"
                                    aria-label="Street Address"
                                    name="street_address"
                                    value={householdData.street_address}
                                    onChange={handleChange}
                                    required
                                    disabled={!isEditable}
                                />
                            </Form.Group>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Postal Code</Form.Label>
                                    <FormControl
                                        placeholder="Postal Code"
                                        aria-label="Postal Code"
                                        name="postal_code"
                                        value={householdData.postal_code}
                                        onChange={handleChange}
                                        required
                                        disabled={!isEditable}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>City</Form.Label>
                                    <FormControl
                                        placeholder="City"
                                        aria-label="City"
                                        name="city"
                                        value={householdData.city}
                                        onChange={handleChange}
                                        required
                                        disabled={!isEditable}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>State</Form.Label>
                                    <FormControl
                                        placeholder="State"
                                        aria-label="State"
                                        name="state"
                                        value={householdData.state}
                                        onChange={handleChange}
                                        required
                                        disabled={!isEditable}
                                    />
                                </Form.Group>
                            </Col>

                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Country</Form.Label>
                                    <Form.Select
                                        aria-label="Country"
                                        name="country"
                                        value={householdData.country}
                                        onChange={handleChange}
                                        required
                                        disabled={!isEditable}
                                    >
                                        {COUNTRY_CHOICES.map((country) => (
                                            <option key={country.value} value={country.value}>
                                                {country.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="d-flex justify-content-between">
                            <Col xs="auto">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={!isEditable && selectedHousehold} // Disable the button if it's not editable
                                >
                                    {selectedHousehold ? 'Update Household' : 'Add New Household'}
                                </Button>
                            </Col>

                            {selectedHousehold && isEditable && (
                                <Col xs="auto">
                                    <Button
                                        variant="danger"
                                        onClick={handleDeleteClick}
                                        disabled={!isEditable && selectedHousehold} // Disable the button if it's not editable
                                    >
                                        Delete
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Confirmation Modal */}
            <ConfirmationModal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmDelete}
                title="Confirm Deletion"
                body="Are you sure you want to delete this household? This action cannot be undone."
            />
        </>
    );
};

export default HouseholdDetails;
